import * as Sentry from '@sentry/react';

export interface PlaceResult {
  name?: string;
  formatted_address?: string;
  address_components?: {
    types: string[];
    long_name: string;
    short_name: string;
  }[];
}

export interface PlacesListener {
  remove: () => void;
}

export interface PredictionRequest {
  input: string;
  types?: string[];
  componentRestrictions?: object;
}

interface MapsRuntime {
  importLibrary?: (name: string) => Promise<unknown>;
  event?: { clearInstanceListeners?: (instance: object) => void };
  places?: {
    AutocompleteService?: new () => {
      getPlacePredictions: (
        request: PredictionRequest,
        callback: (predictions: unknown[] | null, status: string) => void
      ) => unknown;
    };
    Autocomplete: new (
      input: HTMLInputElement,
      options: object
    ) => {
      getPlace: () => PlaceResult;
      addListener: (name: string, callback: () => void) => PlacesListener;
    };
  };
}

export type PlacesFailureReason = 'load' | 'auth' | 'error' | 'timeout';

/**
 * Google calls window.gm_authFailure when it refuses the key or the page's
 * referrer. It fires once per page load, so the refusal is remembered for
 * fields that mount later. Any handler already on the page keeps running.
 */
type AuthWindow = Window & { gm_authFailure?: () => void };
const authListeners = new Set<() => void>();
let authFailed = false;
let authHookInstalled = false;

// Nit 6: a script that loads later and assigns window.gm_authFailure
// itself replaces this hook, so a refusal after that goes unnoticed here
// (the 4 s no-answer check still catches it). Nothing on the page does so
// today; chain to the previous handler if you add one.
export const installAuthFailureHook = () => {
  if (authHookInstalled) {
    return;
  }
  authHookInstalled = true;
  const target = window as AuthWindow;
  const previous = target.gm_authFailure;
  target.gm_authFailure = () => {
    authFailed = true;
    authListeners.forEach((listener) => listener());
    previous?.();
  };
};

export const placesAuthFailed = () => authFailed;

export const onPlacesAuthFailure = (listener: () => void) => {
  installAuthFailureHook();
  authListeners.add(listener);
  return () => {
    authListeners.delete(listener);
  };
};

/** Test-only: forget the hook and the remembered refusal. */
export const resetPlacesAuthForTests = () => {
  authListeners.clear();
  authFailed = false;
  authHookInstalled = false;
};

// One Sentry warning per reason per page load; breadcrumbs every time.
const reportedReasons = new Set<PlacesFailureReason>();

/** Test-only: allow each reason to be reported again. */
export const resetPlacesReportsForTests = () => reportedReasons.clear();

/** Sentry gets the reason only: never the typed address. */
export const reportPlacesFailure = (
  reason: PlacesFailureReason,
  status?: string
) => {
  try {
    Sentry.addBreadcrumb({
      level: 'warning',
      category: 'address-suggestions',
      data: status ? { reason, status } : { reason },
      message: `Address suggestions unavailable: ${reason}`,
    });
    if (reportedReasons.has(reason)) {
      return;
    }
    reportedReasons.add(reason);
    Sentry.captureMessage(`Address suggestions unavailable: ${reason}`, {
      level: 'warning',
      tags: { addressSuggestions: reason },
      extra: status ? { status } : undefined,
    });
  } catch {
    // Monitoring must never get in the way of typing an address.
  }
};

export const getGoogleMaps = () =>
  (window as Window & { google?: { maps?: MapsRuntime } }).google?.maps;

let pending: Promise<void> | undefined;

const placesReady = () => !!getGoogleMaps()?.places?.Autocomplete;

export const loadPlaces = (
  apiKey: string,
  language = 'en',
  libraries = ['places'],
  scriptBaseUrl = 'https://maps.googleapis.com/maps/api/js'
): Promise<void> => {
  installAuthFailureHook();
  if (placesReady()) {
    return Promise.resolve();
  }
  if (pending) {
    return pending;
  }
  if (!apiKey) {
    return Promise.reject(new Error('Address suggestions unavailable'));
  }

  const request = new Promise<void>((resolve, reject) => {
    const existing = Array.from(document.scripts).find((script) =>
      script.src.startsWith(scriptBaseUrl)
    );
    const script = existing ?? document.createElement('script');
    const owned = !existing || script.dataset.clixPlaces === 'true';
    let finished = false;
    let importing = false;
    const cleanup = () => {
      window.clearInterval(poll);
      window.clearTimeout(timeout);
      script.removeEventListener('error', fail);
      script.removeEventListener('load', check);
    };
    const fail = () => {
      if (finished) {
        return;
      }
      finished = true;
      cleanup();
      if (owned) {
        script.remove();
      }
      reject(new Error('Address suggestions unavailable'));
    };
    const check = () => {
      if (finished) {
        return;
      }
      if (placesReady()) {
        finished = true;
        cleanup();
        resolve();
      } else if (!importing && getGoogleMaps()?.importLibrary) {
        importing = true;
        getGoogleMaps()!.importLibrary!('places').then(check).catch(fail);
      }
    };
    const poll = window.setInterval(check, 100);
    const timeout = window.setTimeout(fail, 10000);
    script.addEventListener('error', fail);
    script.addEventListener('load', check);
    if (!existing && !getGoogleMaps()?.importLibrary) {
      const url = new URL(scriptBaseUrl);
      url.searchParams.set('key', apiKey);
      url.searchParams.set('language', language);
      url.searchParams.set(
        'libraries',
        Array.from(new Set([...libraries, 'places'])).join(',')
      );
      script.src = url.toString();
      script.async = true;
      script.dataset.clixPlaces = 'true';
      document.head.appendChild(script);
    }
    check();
  });
  pending = request;
  request.then(
    () => {
      if (pending === request) {
        pending = undefined;
      }
    },
    () => {
      if (pending === request) {
        pending = undefined;
      }
    }
  );
  return request;
};
